import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getMarkPriceHistory } from '@/services/markPriceApi';
import { getIndexPriceHistory } from '@/services/indexPriceApi';
import { getPremiumHistory } from '@/services/premiumIndexApi';
import { getTradingVolume } from '@/services/tradingVolumeApi';
import {
  CandleLine,
  DataDetailLineChart,
  DataDetailPairCandleChart,
  EXCHANGE1_COLOR,
  EXCHANGE2_COLOR,
  ValueLine,
  createChartSyncGroup,
  exchangeToSlug,
  isIndexExchange,
  isMarkExchange,
  isPremiumExchange,
  isTradingVolumeExchange,
  mapMsCandle,
  mapTradingVolumeQuoteLine,
} from '@/lib/dataDetailChartKit';
import '@/pages/Overview/Overview.css';
import './DataDetailPanel.css';

const DEFAULT_HISTORY_INTERVAL = '5m';
const HISTORY_INTERVAL_OPTIONS = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'] as const;
const DATA_DETAIL_LAYOUT_STORAGE_KEY = 'data-detail-layout-mode';

type DataDetailLayoutMode = 'stack' | 'grid';
type DataDetailRefreshKey = 'tradingVolume' | 'premium' | 'mark' | 'index';

type DataDetailErrors = Partial<Record<DataDetailRefreshKey, string>>;

const inputUtcToIso = (value: string): string => {
  if (!value) return '';
  return new Date(`${value}:00Z`).toISOString();
};

function loadLayoutMode(): DataDetailLayoutMode {
  try {
    const stored = localStorage.getItem(DATA_DETAIL_LAYOUT_STORAGE_KEY);
    if (stored === 'stack' || stored === 'grid') return stored;
  } catch {}
  return 'grid';
}

function StackLayoutIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <rect x="2" y="2.5" width="12" height="4.5" rx="1" fill="currentColor" />
      <rect x="2" y="9" width="12" height="4.5" rx="1" fill="currentColor" />
    </svg>
  );
}

function GridLayoutIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <rect x="2" y="2" width="5" height="5" rx="0.8" fill="currentColor" />
      <rect x="9" y="2" width="5" height="5" rx="0.8" fill="currentColor" />
      <rect x="2" y="9" width="5" height="5" rx="0.8" fill="currentColor" />
      <rect x="9" y="9" width="5" height="5" rx="0.8" fill="currentColor" />
    </svg>
  );
}

function DataDetailChartCard({
  title,
  subtitle,
  error,
  onRefresh,
  isRefreshing = false,
  refreshDisabled = false,
  children,
}: {
  title: string;
  subtitle: string;
  error?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshDisabled?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="overview-card data-detail-card">
      <div className="overview-card-title">
        <div>
          <h2>{title}</h2>
          <span>{subtitle}</span>
        </div>
        {onRefresh ? (
          <button
            type="button"
            className="overview-card-refresh"
            onClick={onRefresh}
            disabled={isRefreshing || refreshDisabled}
          >
            {isRefreshing ? '刷新中...' : '刷新'}
          </button>
        ) : null}
      </div>
      {error ? <div className="overview-card-error">{error}</div> : children}
    </section>
  );
}

export interface DataDetailPanelProps {
  symbol: string;
  exchange1: string;
  exchange2: string;
  instId1: string;
  instId2: string;
  startTimeUtc: string;
}

export function DataDetailPanel({
  symbol,
  exchange1,
  exchange2,
  instId1,
  instId2,
  startTimeUtc,
}: DataDetailPanelProps) {
  const [historyInterval, setHistoryInterval] = useState<string>(DEFAULT_HISTORY_INTERVAL);
  const [layoutMode, setLayoutMode] = useState<DataDetailLayoutMode>(() => loadLayoutMode());
  const [isLoading, setIsLoading] = useState(false);
  const [refreshingCharts, setRefreshingCharts] = useState<Partial<Record<DataDetailRefreshKey, boolean>>>({});
  const [errors, setErrors] = useState<DataDetailErrors>({});
  const [tradingVolumeLines, setTradingVolumeLines] = useState<ValueLine[]>([]);
  const [premiumLines, setPremiumLines] = useState<ValueLine[]>([]);
  const [markCandles, setMarkCandles] = useState<CandleLine[]>([]);
  const [indexCandles, setIndexCandles] = useState<CandleLine[]>([]);
  const chartSyncGroupRef = useRef(createChartSyncGroup());
  const querySeqRef = useRef(0);

  const exchange1Slug = exchangeToSlug(exchange1);
  const exchange2Slug = exchangeToSlug(exchange2);
  const pairSubtitle = `${exchange1}:${instId1} / ${exchange2}:${instId2}`;
  const exchangeSubtitle = `${exchange1} / ${exchange2}`;

  const buildQueryContext = useCallback(() => {
    const startTime = inputUtcToIso(startTimeUtc);
    const endTime = new Date().toISOString();
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();
    if (!symbol || !instId1 || !instId2) return null;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) return null;
    return { startTime, endTime, startMs, endMs };
  }, [instId1, instId2, startTimeUtc, symbol]);

  const fetchTradingVolume = useCallback(
    async (ctx: { startTime: string; endTime: string }, querySeq: number) => {
      const value = await Promise.allSettled([
        isTradingVolumeExchange(exchange1Slug)
          ? getTradingVolume({
              exchange: exchange1,
              market_type: 'future',
              symbol,
              inst_id: instId1,
              start_time: ctx.startTime,
              end_time: ctx.endTime,
              limit: 50000,
            })
          : Promise.resolve(null),
        isTradingVolumeExchange(exchange2Slug)
          ? getTradingVolume({
              exchange: exchange2,
              market_type: 'future',
              symbol,
              inst_id: instId2,
              start_time: ctx.startTime,
              end_time: ctx.endTime,
              limit: 50000,
            })
          : Promise.resolve(null),
      ]);
      if (querySeqRef.current !== querySeq) return;
      const [volume1, volume2] = value;
      const v1 = volume1.status === 'fulfilled' ? volume1.value : null;
      const v2 = volume2.status === 'fulfilled' ? volume2.value : null;
      setTradingVolumeLines([
        {
          key: 'trading_volume1',
          label: `${exchange1}:${instId1} 24h quote volume`,
          color: EXCHANGE1_COLOR,
          data: v1 ? mapTradingVolumeQuoteLine(v1.data) : [],
        },
        {
          key: 'trading_volume2',
          label: `${exchange2}:${instId2} 24h quote volume`,
          color: EXCHANGE2_COLOR,
          data: v2 ? mapTradingVolumeQuoteLine(v2.data) : [],
        },
      ]);
      const unsupportedLabels = [
        !isTradingVolumeExchange(exchange1Slug) ? exchange1 : null,
        !isTradingVolumeExchange(exchange2Slug) ? exchange2 : null,
      ].filter((item): item is string => item != null);
      const failedLabels = [
        volume1.status === 'rejected' ? exchange1 : null,
        volume2.status === 'rejected' ? exchange2 : null,
      ].filter((item): item is string => item != null);
      setErrors((prev) => ({
        ...prev,
        tradingVolume:
          unsupportedLabels.length >= 2 || failedLabels.length >= 2
            ? `Trading Volume 查询不可用：${[...unsupportedLabels, ...failedLabels].join(' / ')}`
            : undefined,
      }));
    },
    [exchange1, exchange1Slug, exchange2, exchange2Slug, instId1, instId2, symbol],
  );

  const fetchPremium = useCallback(
    async (ctx: { startMs: number; endMs: number }, querySeq: number) => {
      const value = await Promise.allSettled([
        isPremiumExchange(exchange1Slug)
          ? getPremiumHistory({
              exchange: exchange1Slug,
              symbol,
              interval: historyInterval,
              start_ts: ctx.startMs,
              end_ts: ctx.endMs,
            })
          : Promise.resolve(null),
        isPremiumExchange(exchange2Slug)
          ? getPremiumHistory({
              exchange: exchange2Slug,
              symbol,
              interval: historyInterval,
              start_ts: ctx.startMs,
              end_ts: ctx.endMs,
            })
          : Promise.resolve(null),
      ]);
      if (querySeqRef.current !== querySeq) return;
      const [premium1, premium2] = value;
      const p1 = premium1.status === 'fulfilled' ? premium1.value : null;
      const p2 = premium2.status === 'fulfilled' ? premium2.value : null;
      setPremiumLines([
        {
          key: 'premium1',
          label: `${exchange1} premium`,
          color: EXCHANGE1_COLOR,
          data: p1 ? mapMsCandle(p1.data).map((point) => ({ time: point.time, value: point.close * 100 })) : [],
        },
        {
          key: 'premium2',
          label: `${exchange2} premium`,
          color: EXCHANGE2_COLOR,
          data: p2 ? mapMsCandle(p2.data).map((point) => ({ time: point.time, value: point.close * 100 })) : [],
        },
      ]);
      const failedLabels = [
        premium1.status === 'rejected' ? exchange1 : null,
        premium2.status === 'rejected' ? exchange2 : null,
      ].filter((item): item is string => item != null);
      setErrors((prev) => ({
        ...prev,
        premium: failedLabels.length >= 2 ? `Premium 查询失败：${failedLabels.join(' / ')}` : undefined,
      }));
    },
    [exchange1, exchange1Slug, exchange2, exchange2Slug, historyInterval, symbol],
  );

  const fetchMark = useCallback(
    async (ctx: { startMs: number; endMs: number }, querySeq: number) => {
      const markUnsupported = !isMarkExchange(exchange1Slug) || !isMarkExchange(exchange2Slug);
      const value = await Promise.all([
        isMarkExchange(exchange1Slug)
          ? getMarkPriceHistory({
              exchange: exchange1Slug,
              symbol,
              interval: historyInterval,
              start_ts: ctx.startMs,
              end_ts: ctx.endMs,
            })
          : Promise.resolve(null),
        isMarkExchange(exchange2Slug)
          ? getMarkPriceHistory({
              exchange: exchange2Slug,
              symbol,
              interval: historyInterval,
              start_ts: ctx.startMs,
              end_ts: ctx.endMs,
            })
          : Promise.resolve(null),
      ]);
      if (querySeqRef.current !== querySeq) return;
      const [mark1, mark2] = value;
      setMarkCandles([
        {
          key: 'mark1',
          label: `${exchange1} mark`,
          color: EXCHANGE1_COLOR,
          data: mark1 ? mapMsCandle(mark1.data) : [],
        },
        {
          key: 'mark2',
          label: `${exchange2} mark`,
          color: EXCHANGE2_COLOR,
          data: mark2 ? mapMsCandle(mark2.data) : [],
        },
      ]);
      setErrors((prev) => ({
        ...prev,
        mark: markUnsupported
          ? 'Mark Price 仅支持 Bybit / OKX / Binance / Gateio / Bitget；不支持的交易所会显示为空。'
          : undefined,
      }));
    },
    [exchange1, exchange1Slug, exchange2, exchange2Slug, historyInterval, symbol],
  );

  const fetchIndex = useCallback(
    async (ctx: { startMs: number; endMs: number }, querySeq: number) => {
      const indexUnsupported = !isIndexExchange(exchange1Slug) || !isIndexExchange(exchange2Slug);
      const value = await Promise.all([
        isIndexExchange(exchange1Slug)
          ? getIndexPriceHistory({
              exchange: exchange1Slug,
              symbol,
              interval: historyInterval,
              start_ts: ctx.startMs,
              end_ts: ctx.endMs,
            })
          : Promise.resolve(null),
        isIndexExchange(exchange2Slug)
          ? getIndexPriceHistory({
              exchange: exchange2Slug,
              symbol,
              interval: historyInterval,
              start_ts: ctx.startMs,
              end_ts: ctx.endMs,
            })
          : Promise.resolve(null),
      ]);
      if (querySeqRef.current !== querySeq) return;
      const [index1, index2] = value;
      setIndexCandles([
        {
          key: 'index1',
          label: `${exchange1} index`,
          color: EXCHANGE1_COLOR,
          data: index1 ? mapMsCandle(index1.data) : [],
        },
        {
          key: 'index2',
          label: `${exchange2} index`,
          color: EXCHANGE2_COLOR,
          data: index2 ? mapMsCandle(index2.data) : [],
        },
      ]);
      setErrors((prev) => ({
        ...prev,
        index: indexUnsupported
          ? 'Index Price 仅支持 Bybit / OKX / Binance / Gateio / Bitget；不支持的交易所会显示为空。'
          : undefined,
      }));
    },
    [exchange1, exchange1Slug, exchange2, exchange2Slug, historyInterval, symbol],
  );

  const runQuery = useCallback(() => {
    const ctx = buildQueryContext();
    if (!ctx) return;

    const querySeq = querySeqRef.current + 1;
    querySeqRef.current = querySeq;
    setIsLoading(true);
    setErrors({});

    let pending = 4;
    const finishTask = () => {
      pending -= 1;
      if (pending <= 0 && querySeqRef.current === querySeq) {
        setIsLoading(false);
      }
    };

    void fetchTradingVolume(ctx, querySeq).catch((reason: unknown) => {
      if (querySeqRef.current !== querySeq) return;
      setTradingVolumeLines([]);
      setErrors((prev) => ({
        ...prev,
        tradingVolume: reason instanceof Error ? reason.message : String(reason),
      }));
    }).finally(finishTask);

    void fetchPremium(ctx, querySeq).catch((reason: unknown) => {
      if (querySeqRef.current !== querySeq) return;
      setPremiumLines([]);
      setErrors((prev) => ({
        ...prev,
        premium: reason instanceof Error ? reason.message : String(reason),
      }));
    }).finally(finishTask);

    void fetchMark(ctx, querySeq).catch((reason: unknown) => {
      if (querySeqRef.current !== querySeq) return;
      setMarkCandles([]);
      setErrors((prev) => ({
        ...prev,
        mark: reason instanceof Error ? reason.message : String(reason),
      }));
    }).finally(finishTask);

    void fetchIndex(ctx, querySeq).catch((reason: unknown) => {
      if (querySeqRef.current !== querySeq) return;
      setIndexCandles([]);
      setErrors((prev) => ({
        ...prev,
        index: reason instanceof Error ? reason.message : String(reason),
      }));
    }).finally(finishTask);
  }, [buildQueryContext, fetchIndex, fetchMark, fetchPremium, fetchTradingVolume]);

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  const selectLayoutMode = useCallback((mode: DataDetailLayoutMode) => {
    setLayoutMode(mode);
    try {
      localStorage.setItem(DATA_DETAIL_LAYOUT_STORAGE_KEY, mode);
    } catch {}
  }, []);

  const refreshChart = useCallback(
    (key: DataDetailRefreshKey) => {
      const ctx = buildQueryContext();
      if (!ctx) return;

      const querySeq = querySeqRef.current;
      setRefreshingCharts((prev) => ({ ...prev, [key]: true }));
      const finishRefresh = () => {
        setRefreshingCharts((prev) => ({ ...prev, [key]: false }));
      };
      const setRefreshError = (errorKey: DataDetailRefreshKey, reason: unknown) => {
        setErrors((prev) => ({
          ...prev,
          [errorKey]: reason instanceof Error ? reason.message : String(reason),
        }));
      };

      if (key === 'tradingVolume') {
        void fetchTradingVolume(ctx, querySeq)
          .then(() => setErrors((prev) => ({ ...prev, tradingVolume: undefined })))
          .catch((reason) => setRefreshError('tradingVolume', reason))
          .finally(finishRefresh);
        return;
      }
      if (key === 'premium') {
        void fetchPremium(ctx, querySeq)
          .then(() => setErrors((prev) => ({ ...prev, premium: undefined })))
          .catch((reason) => setRefreshError('premium', reason))
          .finally(finishRefresh);
        return;
      }
      if (key === 'mark') {
        void fetchMark(ctx, querySeq)
          .then(() => setErrors((prev) => ({ ...prev, mark: undefined })))
          .catch((reason) => setRefreshError('mark', reason))
          .finally(finishRefresh);
        return;
      }
      void fetchIndex(ctx, querySeq)
        .then(() => setErrors((prev) => ({ ...prev, index: undefined })))
        .catch((reason) => setRefreshError('index', reason))
        .finally(finishRefresh);
    },
    [buildQueryContext, fetchIndex, fetchMark, fetchPremium, fetchTradingVolume],
  );

  const renderCandleChart = (lines: CandleLine[]) =>
    lines.some((line) => line.data.length > 0) ? (
      <>
        <div className="overview-legend">
          {lines.map((line) => (
            <span key={line.key}>
              <i className="overview-dot" style={{ background: line.color }} />
              {line.label}
            </span>
          ))}
        </div>
        <DataDetailPairCandleChart series={lines} syncGroupRef={chartSyncGroupRef} />
      </>
    ) : (
      <div className="overview-empty">暂无数据，请调整参数后查询。</div>
    );

  const renderLineChart = (lines: ValueLine[], valueSuffix = '%', valuePrecision = 6) =>
    lines.some((line) => line.data.length > 0) ? (
      <>
        <div className="overview-legend">
          {lines.map((line) => (
            <span key={line.key}>
              <i className="overview-dot" style={{ background: line.color }} />
              {line.label}
            </span>
          ))}
        </div>
        <DataDetailLineChart
          lines={lines}
          syncGroupRef={chartSyncGroupRef}
          valueSuffix={valueSuffix}
          valuePrecision={valuePrecision}
        />
      </>
    ) : (
      <div className="overview-empty">暂无数据，请调整参数后查询。</div>
    );

  const loadingLabel = useMemo(() => (isLoading ? '数据加载中…' : null), [isLoading]);

  return (
    <div className="data-detail-panel">
      <div className="data-detail-toolbar">
        <label className="data-detail-toolbar-field">
          <span>历史 interval</span>
          <select
            value={historyInterval}
            onChange={(event) => setHistoryInterval(event.target.value)}
            disabled={isLoading}
          >
            {HISTORY_INTERVAL_OPTIONS.map((interval) => (
              <option key={interval} value={interval}>
                {interval}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="data-detail-toolbar-btn" onClick={runQuery} disabled={isLoading}>
          {isLoading ? '查询中...' : '刷新全部'}
        </button>
        {loadingLabel ? <span className="data-detail-toolbar-hint">{loadingLabel}</span> : null}
        <div className="data-detail-layout-toggle" role="group" aria-label="图表布局">
          <button
            type="button"
            className={layoutMode === 'stack' ? 'is-active' : undefined}
            aria-pressed={layoutMode === 'stack'}
            title="分栏显示"
            onClick={() => selectLayoutMode('stack')}
          >
            <StackLayoutIcon />
          </button>
          <button
            type="button"
            className={layoutMode === 'grid' ? 'is-active' : undefined}
            aria-pressed={layoutMode === 'grid'}
            title="田字格显示"
            onClick={() => selectLayoutMode('grid')}
          >
            <GridLayoutIcon />
          </button>
        </div>
      </div>

      <div className={`data-detail-grid${layoutMode === 'grid' ? ' is-grid' : ' is-stack'}`}>
        <DataDetailChartCard
          title="Trading Volume 24h"
          subtitle={`${pairSubtitle} | future`}
          error={errors.tradingVolume}
          onRefresh={() => refreshChart('tradingVolume')}
          isRefreshing={Boolean(refreshingCharts.tradingVolume)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(tradingVolumeLines, ' quote')}
        </DataDetailChartCard>

        <DataDetailChartCard
          title="Premium 历史"
          subtitle={exchangeSubtitle}
          error={errors.premium}
          onRefresh={() => refreshChart('premium')}
          isRefreshing={Boolean(refreshingCharts.premium)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(premiumLines)}
        </DataDetailChartCard>

        <DataDetailChartCard
          title="Mark Price K 线"
          subtitle={exchangeSubtitle}
          error={errors.mark}
          onRefresh={() => refreshChart('mark')}
          isRefreshing={Boolean(refreshingCharts.mark)}
          refreshDisabled={isLoading}
        >
          {renderCandleChart(markCandles)}
        </DataDetailChartCard>

        <DataDetailChartCard
          title="Index Price K 线"
          subtitle={exchangeSubtitle}
          error={errors.index}
          onRefresh={() => refreshChart('index')}
          isRefreshing={Boolean(refreshingCharts.index)}
          refreshDisabled={isLoading}
        >
          {renderCandleChart(indexCandles)}
        </DataDetailChartCard>
      </div>
    </div>
  );
}
